import React, { useRef, useLayoutEffect, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PageWrapper from './PageWrapper';
import { selectPageSlice } from '../Selector';
import { reportPageCount } from '../../../redux/actions';

// Must match index.scss variables exactly
const PAGE_HEIGHT = 1123;
const PADDING_TOP = 50;
const PADDING_BOTTOM = 60;
const FOOTER_HEIGHT = 40;
const CONTENT_HEIGHT = PAGE_HEIGHT - PADDING_TOP - PADDING_BOTTOM - FOOTER_HEIGHT; // 973px
const CONTENT_WIDTH = 794 - 60 - 60; // 674px  (794 - 2 * horizontal padding)

/**
 * Distributes a flat list of items across one or more A4 pages.
 *
 * `header` is rendered only on the FIRST page.  Continuation pages use the
 * full content height so the page fills completely before overflowing.
 *
 * Props
 *   header          ReactElement   – rendered only on the first page
 *   items           ReactElement[] – individual content elements to distribute
 *   startPageNumber number         – page number printed on the first page
 *   personName      string         – name shown in the footer
 *   sliceKey        string?        – pageContent discriminator to re-measure on
 */
function MultiPage({ header, items, startPageNumber, personName, sliceKey, slice, sectionKey, reportPageCount }) {
  const measureRef = useRef(null);
  const [pages, setPages] = useState(null);

  const isFirstSliceRef = useRef(true);
  useEffect(() => {
    if (isFirstSliceRef.current) {
      isFirstSliceRef.current = false;
      return;
    }
    setPages(null);
  }, [slice]);

  useLayoutEffect(() => {
    if (pages) return;
    if (!measureRef.current) return;

    const container = measureRef.current;
    const headerEl = container.querySelector('[data-mp="header"]');
    const itemEls = Array.from(container.querySelectorAll('[data-mp="item"]'));

    const headerH = headerEl ? headerEl.getBoundingClientRect().height : 0;

    const page1Available = CONTENT_HEIGHT - headerH;
    const restAvailable = CONTENT_HEIGHT;

    const allPages = [];
    let pageItems = [];
    let usedH = 0;
    let isFirstPage = true;

    items.forEach((item, idx) => {
      const h = itemEls[idx] ? itemEls[idx].getBoundingClientRect().height : 0;
      const avail = isFirstPage ? page1Available : restAvailable;

      if (pageItems.length > 0 && usedH + h > avail) {
        allPages.push(pageItems);
        pageItems = [item];
        usedH = h;
        isFirstPage = false;
      } else {
        pageItems.push(item);
        usedH += h;
      }
    });

    if (pageItems.length > 0) {
      allPages.push(pageItems);
    }

    const groups = allPages.length > 0 ? allPages : [items];
    setPages(groups);

    if (sectionKey && reportPageCount) {
      reportPageCount(sectionKey, groups.length);
    }
  }, [pages, items, sectionKey, reportPageCount]);

  // ── Phase 1: measurement ───────────────────────────────────────────────────
  if (!pages) {
    return (
      <div style={{ position: 'relative', height: 0, overflow: 'hidden' }}>
        <div
          ref={measureRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: CONTENT_WIDTH,
            visibility: 'hidden',
            pointerEvents: 'none',
          }}
        >
          {header && <div data-mp="header">{header}</div>}
          {items.map((item, i) => (
            <div key={i} data-mp="item">
              {item}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Phase 2: render ────────────────────────────────────────────────────────
  return (
    <>
      {pages.map((group, idx) => (
        <PageWrapper
          key={idx}
          pageNumber={startPageNumber + idx}
          personName={personName}
        >
          {idx === 0 && header}
          {group}
        </PageWrapper>
      ))}
    </>
  );
}

const mapStateToProps = (state, ownProps) => ({
  slice: selectPageSlice(state, ownProps.sliceKey || null),
});

const mapDispatchToProps = (dispatch) => ({
  reportPageCount: (sectionKey, count) => dispatch(reportPageCount(sectionKey, count)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MultiPage);
